import SignaturePad from 'signature_pad'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

export interface SignaturePadHandle {
  clear: () => void
  isEmpty: () => boolean
  toDataURL: (type?: string) => string
}

interface Props {
  canvasProps?: React.CanvasHTMLAttributes<HTMLCanvasElement>
  penColor?: string
  backgroundColor?: string
  onEnd?: () => void
}

export const SignatureCanvas = forwardRef<SignaturePadHandle, Props>(
  ({ canvasProps, penColor = 'black', backgroundColor = 'white', onEnd }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const padRef = useRef<SignaturePad | null>(null)
    const initOptsRef = useRef({ penColor, backgroundColor })

    useEffect(() => {
      const canvas = canvasRef.current
      if (!canvas) return

      const pad = new SignaturePad(canvas, initOptsRef.current)
      padRef.current = pad

      const resize = () => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1)
        const width = Math.round(canvas.offsetWidth * ratio)
        const height = Math.round(canvas.offsetHeight * ratio)

        // ResizeObserver fires on the first layout pass too, when the element
        // has no size yet; writing 0 here would leave an unusable bitmap.
        if (width === 0 || height === 0) return
        if (canvas.width === width && canvas.height === height) return

        // Assigning width or height wipes the bitmap and resets the transform,
        // so anything already drawn has to be re-applied. Without this, a phone
        // keyboard opening — or the URL bar collapsing — silently erased a
        // signature the member had already drawn. The points are stored in CSS
        // pixels, so they land correctly against the new scale.
        const strokes = pad.toData()
        canvas.width = width
        canvas.height = height
        canvas.getContext('2d')?.scale(ratio, ratio)
        pad.clear()
        if (strokes.length > 0) pad.fromData(strokes)
      }

      const ro = new ResizeObserver(resize)
      ro.observe(canvas)
      resize()

      return () => {
        pad.off()
        ro.disconnect()
      }
    }, [])

    useEffect(() => {
      const pad = padRef.current
      if (!pad || !onEnd) return
      pad.addEventListener('endStroke', onEnd)
      return () => pad.removeEventListener('endStroke', onEnd)
    }, [onEnd])

    useImperativeHandle(ref, () => ({
      clear: () => padRef.current?.clear(),
      isEmpty: () => padRef.current?.isEmpty() ?? true,
      toDataURL: (type) => padRef.current?.toDataURL(type) ?? '',
    }))

    return <canvas ref={canvasRef} {...canvasProps} />
  },
)

SignatureCanvas.displayName = 'SignatureCanvas'
